/**
 * ONE RELEASE as a folder tile — 9.3, 10.2, 11 — on the Releases page.
 *
 * The command center's `ReleaseFolderCard`, minus its Edit and Delete
 * buttons: a reseller reads releases, it never curates them. The tile
 * answers the three questions asked of a release without opening it — has it
 * shipped, how many versions are in it, and which is the newest — and
 * clicking it goes to the release's own page.
 */
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Folder } from "lucide-react";
import type { JSX } from "react";

import type { ReleaseFamily } from "./release-families.js";
import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";

interface Props {
  family: ReleaseFamily;
  onOpen: () => void;
}

export function ReleaseFolderCard({ family, onOpen }: Props): JSX.Element {
  const { line, builds, hasLine } = family;
  const count = hasLine ? builds.length : 1;
  const newest = builds[0];

  return (
    <Box
      data-testid={`release-folder-${line.version}`}
      sx={{
        position: "relative",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": { borderColor: "primary.main", boxShadow: 1 },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={`Open release ${line.version}`}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          gap: 1.5,
          textAlign: "left",
          border: 0,
          bgcolor: "transparent",
          px: 2,
          py: 1.75,
          cursor: "pointer",
          font: "inherit",
          color: "inherit",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: 1,
            flexShrink: 0,
            color: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          }}
        >
          <Folder size={20} />
        </Box>
        <Box component="span" sx={{ display: "block", minWidth: 0, flex: 1 }}>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{
                fontFamily: (t) => t.typography.h6.fontFamily,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "text.primary",
              }}
            >
              {line.version}
            </Box>
            <Chip
              size="small"
              variant={line.status === "released" ? "filled" : "outlined"}
              color={RELEASE_STATUS_COLOR[line.status] ?? "default"}
              label={statusLabel(line.status)}
              sx={{ height: 20 }}
            />
            {!hasLine && (
              <Tooltip title="This version's release is not on this page — it stands on its own.">
                <Chip size="small" variant="outlined" label="Unfiled" sx={{ height: 20 }} />
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            {line.product_name ? `${line.product_name} · ` : ""}
            {count === 0 ? "No versions yet" : `${count} version${count === 1 ? "" : "s"}`}
            {newest ? ` · newest ${newest.version}` : ""}
          </Typography>
          {line.description && (
            <Typography
              sx={{
                fontSize: 12,
                color: "text.disabled",
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {line.description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
